import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Crosshair, Loader2, Check } from "lucide-react";
import { api } from "@/lib/api";
import { tr } from "@/lib/utils";

// User-facing model: City -> Area/Neighbourhood. Governorates stay internal (carried as governorate_id for the backend only).
export const LocationPicker = ({ value, onChange, showGps = true, compact = false }) => {
  const { t } = useTranslation();
  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);
  const [cq, setCq] = useState("");
  const [q, setQ] = useState("");
  const [gps, setGps] = useState("idle");
  const v = value || {};

  useEffect(() => { api.get("/locations/cities").then((r) => setCities(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (!v.city_id) { setAreas([]); return; } api.get("/locations/areas", { params: { city_id: v.city_id, limit: 1000 } }).then((r) => setAreas(r.data)); }, [v.city_id]);
  const nz = (s) => (s || "").replace(/[\u064B-\u0652\u0670\u0640]/g, "").replace(/\s+/g, "");
  const match = (x, s) => !s || nz(x.name_ar).includes(nz(s)) || (x.name_en || "").toLowerCase().includes(s.toLowerCase());
  const filteredCities = useMemo(() => cities.filter((c) => match(c, cq) || c.id === v.city_id), [cities, cq, v.city_id]);
  const filtered = useMemo(() => areas.filter((a) => match(a, q)), [areas, q]);

  const pickCity = (id) => { const c = cities.find((x) => x.id === id); onChange({ ...v, city_id: id, governorate_id: c?.governorate_id || "", area_id: "", city: c, area: null, lat: undefined, lng: undefined }); setQ(""); };

  const locate = () => {
    if (!navigator.geolocation) { setGps("error"); return; }
    setGps("loading");
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude: lat, longitude: lng } = pos.coords;
      try {
        const { data } = await api.get("/locations/reverse", { params: { lat, lng } });
        const cityId = data.area?.city_id || data.city?.id || "";
        onChange({ governorate_id: data.governorate?.id || data.city?.governorate_id || "", city_id: cityId, area_id: data.area?.id || "", lat, lng, city: data.city || cities.find((c) => c.id === cityId), area: data.area });
        setGps("done");
      } catch { setGps("error"); }
    }, () => setGps("error"), { enableHighAccuracy: true, timeout: 10000 });
  };

  return (
    <div className={compact ? "grid gap-3 sm:grid-cols-2" : "grid gap-4 md:grid-cols-2"}>
      <div>
        <label className="z-label">{t("loc.city")}</label>
        <input data-testid="city-search" className="z-input mb-2 !py-2 text-sm" placeholder={t("loc.search_city")} value={cq} onChange={(e) => setCq(e.target.value)} />
        <select data-testid="select-city" className="z-input" value={v.city_id || ""} onChange={(e) => pickCity(e.target.value)}>
          <option value="">{t("loc.select")}</option>
          {filteredCities.map((c) => <option key={c.id} value={c.id}>{tr(c)}</option>)}
        </select>
      </div>
      <div>
        <label className="z-label">{t("loc.area")}</label>
        <input data-testid="area-search" className="z-input mb-2 !py-2 text-sm" placeholder={t("loc.search_area")} value={q} onChange={(e) => setQ(e.target.value)} disabled={!v.city_id} />
        <select data-testid="select-area" className="z-input" disabled={!v.city_id} value={v.area_id || ""} onChange={(e) => { const a = areas.find((x) => x.id === e.target.value); onChange({ ...v, area_id: e.target.value, governorate_id: a?.governorate_id || v.governorate_id, area: a }); }}>
          <option value="">{v.city_id ? t("loc.select") : t("loc.pick_city_first")}</option>
          {filtered.map((a) => <option key={a.id} value={a.id}>{tr(a)}</option>)}
        </select>
      </div>
      {showGps && (
        <div className="sm:col-span-2 md:col-span-2">
          <button type="button" data-testid="gps-button" onClick={locate} className="inline-flex items-center gap-2 rounded-full border-2 border-[#06B6D4]/40 px-4 py-2 text-sm font-bold text-[#0B192C] transition-colors hover:bg-[#06B6D4]/10">
            {gps === "loading" ? <Loader2 size={16} className="animate-spin" /> : gps === "done" ? <Check size={16} className="text-[#10B981]" /> : <Crosshair size={16} />}
            {gps === "loading" ? t("loc.locating") : gps === "done" ? t("loc.located") : t("loc.gps")}
          </button>
          <p className="mt-1.5 text-xs text-muted-foreground">{gps === "error" ? t("loc.gps_error") : t("loc.gps_hint")}</p>
        </div>
      )}
    </div>
  );
};
